import { html, nothing, type TemplateResult } from "lit";
import type { ChatTabLabelHint } from "../chat/chat-tab-labels.ts";
import type { SessionsListResult } from "../types.ts";
import type { SessionShareFormat } from "./session-share.ts";
import { CHAT_SESSION_TAB_HINTS } from "../control-hints.ts";
import {
  focusPathRow,
  renderRowActions,
  renderSessionPathNavigator,
} from "./session-path-navigator.ts";

export type FleetSessionCatalogRowProps = {
  sessionKey: string;
  openTabs: string[];
  sessions: SessionsListResult | null;
  connected: boolean;
  tabLabelHints?: Record<string, ChatTabLabelHint>;
  onSelectSession: (sessionKey: string) => void;
  onNewEmptySession?: () => void;
  onArchiveAndNew?: () => void;
  onManageSessions?: () => void;
  onShowArchivedSessions?: () => void;
  onForkTab?: () => void;
  onNewAgent?: () => void;
  onOpenAgents?: () => void;
  /** Callback when user requests to share the session (copy link). */
  onShareSession?: (sessionKey: string) => void;
  /** Callback when user requests to export the session transcript. */
  onExportSession?: (sessionKey: string, format: SessionShareFormat) => void;
  /** Callback when user requests to preview the session. */
  onPreviewSession?: (sessionKey: string) => void;
};

function renderAgentRowActions(
  props: FleetSessionCatalogRowProps,
): TemplateResult | typeof nothing {
  if (!props.onNewAgent && !props.onOpenAgents) {
    return nothing;
  }
  return renderRowActions(
    "agent",
    html`
    ${
      props.onNewAgent
        ? html`
          <button
            type="button"
            class="chat-session-tabs__new"
            ?disabled=${!props.connected}
            title=${CHAT_SESSION_TAB_HINTS.newAgent}
            aria-label="New agent"
            @click=${props.onNewAgent}
          >
            <span class="chat-session-tabs__new-icon" aria-hidden="true">+</span>
            <span class="chat-session-tabs__new-text">New agent</span>
          </button>
        `
        : nothing
    }
    ${
      props.onOpenAgents
        ? html`
          <button
            type="button"
            class="chat-session-tabs__manage"
            title=${CHAT_SESSION_TAB_HINTS.manageAgents}
            aria-label="Open Agents tab"
            @click=${props.onOpenAgents}
          >
            Agents
          </button>
        `
        : nothing
    }
  `,
    "Agent row actions",
  );
}

function renderKindRowActions(props: FleetSessionCatalogRowProps): TemplateResult | typeof nothing {
  if (!props.onForkTab && !props.onNewEmptySession) {
    return nothing;
  }
  return renderRowActions(
    "kind",
    html`
    ${
      props.onForkTab
        ? html`
          <button
            type="button"
            class="chat-session-tabs__new"
            ?disabled=${!props.connected}
            title=${CHAT_SESSION_TAB_HINTS.newKind}
            aria-label="New fork session"
            @click=${props.onForkTab}
          >
            <span class="chat-session-tabs__new-icon" aria-hidden="true">+</span>
            <span class="chat-session-tabs__new-text">New kind</span>
          </button>
        `
        : nothing
    }
    <button
      type="button"
      class="chat-session-tabs__manage"
      title=${CHAT_SESSION_TAB_HINTS.allKinds}
      aria-label="Show all kind options in this row"
      @click=${() => focusPathRow(2)}
    >
      All kinds
    </button>
  `,
    "Kind row actions",
  );
}

function renderIdRowActions(props: FleetSessionCatalogRowProps): TemplateResult | typeof nothing {
  if (
    !props.onNewEmptySession &&
    !props.onArchiveAndNew &&
    !props.onManageSessions &&
    !props.onShowArchivedSessions &&
    !props.onShareSession &&
    !props.onExportSession
  ) {
    return nothing;
  }
  return renderRowActions(
    "id",
    html`
    ${
      props.onShareSession
        ? html`
          <button
            type="button"
            class="chat-session-tabs__manage"
            title="Share this session"
            aria-label="Share session"
            @click=${() => props.onShareSession?.(props.sessionKey)}
          >
            Share
          </button>
        `
        : nothing
    }
    ${
      props.onExportSession
        ? html`
          <button
            type="button"
            class="chat-session-tabs__manage"
            title="Export transcript"
            aria-label="Export transcript"
            @click=${() => props.onExportSession?.(props.sessionKey, "json")}
          >
            Export
          </button>
        `
        : nothing
    }
    ${
      props.onNewEmptySession
        ? html`
          <button
            type="button"
            class="chat-session-tabs__new"
            ?disabled=${!props.connected}
            title=${CHAT_SESSION_TAB_HINTS.newTab}
            aria-label="New empty session"
            @click=${props.onNewEmptySession}
          >
            <span class="chat-session-tabs__new-icon" aria-hidden="true">+</span>
            <span class="chat-session-tabs__new-text">New session</span>
          </button>
        `
        : nothing
    }
    ${
      props.onShowArchivedSessions
        ? html`
          <button
            type="button"
            class="chat-session-tabs__manage"
            title=${CHAT_SESSION_TAB_HINTS.restoreArchived}
            aria-label="Show archived chats for restore"
            @click=${props.onShowArchivedSessions}
          >
            Restore
          </button>
        `
        : nothing
    }
    ${
      props.onArchiveAndNew
        ? html`
          <button
            type="button"
            class="chat-session-tabs__manage"
            ?disabled=${!props.connected}
            title=${CHAT_SESSION_TAB_HINTS.archiveAndNew}
            aria-label="Archive current chat and start new"
            @click=${props.onArchiveAndNew}
          >
            Archive & new
          </button>
        `
        : nothing
    }
    ${
      props.onManageSessions
        ? html`
          <button
            type="button"
            class="chat-session-tabs__manage"
            title=${CHAT_SESSION_TAB_HINTS.manageSessions}
            aria-label="Open chat history"
            @click=${props.onManageSessions}
          >
            All chats
          </button>
        `
        : nothing
    }
  `,
    "Id row actions",
  );
}

export function renderFleetSessionCatalogRow(props: FleetSessionCatalogRowProps) {
  const activeKey = props.sessionKey.trim();

  return renderSessionPathNavigator({
    activeSessionKey: activeKey,
    openTabs: props.openTabs,
    sessions: props.sessions,
    hints: props.tabLabelHints,
    onSelectSession: props.onSelectSession,
    showActiveFooter: true,
    ariaLabel: "Session key navigator",
    className: "fleet-session-path",
    rowActions: {
      agent: renderAgentRowActions(props),
      kind: renderKindRowActions(props),
      id: renderIdRowActions(props),
    },
  });
}

export { renderPathRow } from "./session-path-navigator.ts";
